import Image from 'app/Components/Shared/Image/image';
import React, { useEffect, useRef } from 'react';
import safePlay from './utils/safePlay';
import Config from 'app/Config';

const HeroCarouselSlide = ({ item, index, currentIndex, isMuted, isPlaying, pausedIntentRef, swiperRef, onVideoPlayPause, onVideoStateChange, onVideoClick }) => {
    const localRef = useRef(null);
    const firedRef = useRef(false);

    const slideIsVideo = String(item?.mediaType?.value || '').toLowerCase() === 'video';
    const isDesktopView = typeof window !== 'undefined' ? window.innerWidth >= 769 : true;

    const url = slideIsVideo 
        ? (Config.epiBaseUrl + (isDesktopView 
            ? item?.desktopVideo?.expandedValue?.url 
            : item?.mobileVideo?.expandedValue?.url))
        : (Config.epiBaseUrl + (isDesktopView 
            ? item?.desktopImage?.expandedValue?.url 
            : item?.mobileImage?.expandedValue?.url));

    // Get ALT text for images
    const altText = !slideIsVideo ? (item?.imageAltText?.value || item?.titleText?.value || '') : '';
    
    // Get link if available
    const linkUrl = item?.link?.value;

    // Reset firedRef when currentIndex changes (slide becomes inactive)
    useEffect(() => {
        if (index !== currentIndex) {
            firedRef.current = false;
        }
    }, [currentIndex, index]);

    useEffect(() => {
        const el = localRef.current;
        if (!el || !slideIsVideo) return undefined;

        const update = () => onVideoStateChange(index, !el.paused);
        const onPlay = () => {
            firedRef.current = false;
            update();
        };
        const onPlaying = () => update();
        const onPause = () => update();
        const onCanPlay = () => update();
        const onLoaded = () => update();

        const onTimeUpdate = () => {
            try {
                if (!el || !el.duration || el.duration === Infinity) return;
                const remaining = el.duration - el.currentTime;
                // if within 200ms of end and we haven't fired yet, advance
                if (remaining <= 0.2 && !firedRef.current && index === currentIndex) {
                    // Don't advance if carousel is paused
                    if (!isPlaying) return;
                    
                    firedRef.current = true;
                    const s = swiperRef.current;
                    try { setTimeout(() => { s?.slideNext?.(); }, 50); } catch (e) { /* ignore */ }
                }
            } catch (e) {
                /* ignore */
            }
        };

        el.addEventListener('play', onPlay);
        el.addEventListener('playing', onPlaying);
        el.addEventListener('pause', onPause);
        el.addEventListener('canplay', onCanPlay);
        el.addEventListener('loadedmetadata', onLoaded);
        el.addEventListener('timeupdate', onTimeUpdate);

        if ('muted' in el) el.muted = isMuted;

        // Auto-play active video unless user explicitly paused this slide
        const userPaused = !!pausedIntentRef.current[index];
        if (index === currentIndex && slideIsVideo && !userPaused) {
            let attempts = 0;
            const maxAttempts = 8;
            const tryPlay = () => {
                attempts += 1;
                safePlay(el).then(() => {
                    // success
                }).catch(() => {
                    if (attempts < maxAttempts) requestAnimationFrame(tryPlay);
                });
            };
            tryPlay();
        }

        return () => {
            el.removeEventListener('play', onPlay);
            el.removeEventListener('playing', onPlaying);
            el.removeEventListener('pause', onPause);
            el.removeEventListener('canplay', onCanPlay);
            el.removeEventListener('loadedmetadata', onLoaded);
            el.removeEventListener('timeupdate', onTimeUpdate);
        };
    }, [index, currentIndex, isMuted, isPlaying, pausedIntentRef, slideIsVideo, swiperRef, onVideoStateChange]);

    if (slideIsVideo) {
        return (
            <>
                <video
                    ref={localRef}
                    className="carousel-media"
                    src={url}
                    autoPlay={index === currentIndex}
                    muted={isMuted}
                    playsInline
                    loop={!isPlaying}
                    preload="metadata"
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                        margin: 0,
                        padding: 0
                    }}
                    onEnded={() => {
                        const s = swiperRef.current;
                        if (currentIndex !== index) return;
                        
                        // Don't advance to next slide if carousel is paused
                        if (!isPlaying) return;
                        
                        try { s?.autoplay?.stop?.(); } catch (e) { /* ignore */ }
                        try {
                            setTimeout(() => {
                                try { s?.slideNext?.(); } catch (e) { /* ignore */ }
                            }, 50);
                        } catch (e) { /* ignore */ }
                        firedRef.current = true;

                        const nextIndex = (index + 1) % (Array.isArray(swiperRef.current?.slides) ? swiperRef.current.slides.length : 1);
                        const nextIsVideo = String((swiperRef.current?.slides?.[nextIndex]?.dataset?.mediaType || '').toLowerCase()) === 'video';
                        if (isPlaying && !nextIsVideo) {
                            setTimeout(() => { try { s?.autoplay?.start?.(); } catch (e) { /* ignore */ } }, 120);
                        }
                    }}
                />
                {/* Clickable area for navigation - Desktop: right side, Mobile: top half */}
                {linkUrl && (
                    <a 
                        href={linkUrl} 
                        className="carousel-video-clickable-area"
                        onClick={(e) => {
                            // Let the link navigate
                            e.stopPropagation();
                        }}
                    ></a>
                )}
                {/* Video control area - Desktop: left side, Mobile: bottom half */}
                <div 
                    className="carousel-video-control-area"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onVideoClick();
                    }}
                ></div>
            </>
        );
    }

    // Image/content slide
    const imgStyle = {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
        margin: 0,
        padding: 0,
        objectPosition: isDesktopView ? '61% bottom' : 'top',
    };

    const imageElement = (
        <Image className="carousel-media" src={url} alt={altText} style={imgStyle} />
    );

    // If there's a link, wrap in anchor tag and add clickable area
    if (item?.link?.value) {
        return (
            <a href={item.link.value} className="carousel-slide-link">
                {imageElement}
                <div className="carousel-clickable-area"></div>
            </a>
        );
    }

    return imageElement;
};

export default HeroCarouselSlide;
